import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import type { Dayjs } from "dayjs";
import { useSearchParams } from "react-router";
import DataFilter from "../../components/common/DataFilter";
import { presets, resolveRangeFromParams, type PresetKey } from "../../components/common/dataFilterPresets";
import { useApi } from "../../hooks/useApi";
import { useCompanyId } from "../../hooks/useCompanyId";
import { useBrandId } from "../../hooks/useBrandId";
import DeflectionsKpiRow from "../../components/dashboard/deflections/DeflectionsKpiRow";
import ApiCallsChart from "../../components/dashboard/deflections/ApiCallsChart";
import DeflectionsBarChart from "../../components/dashboard/deflections/DeflectionsBarChart";
import DeflectionsInternalSection from "../../components/dashboard/deflections/DeflectionsInternalSection";
import { useDeflectionProcessors } from "../../hooks/useDeflectionProcessors";
import { type DeflectionsDashboardResponse } from "../../types/deflections";

export default function DeflectionsTab() {
  const { get, loading } = useApi();
  const { companyId } = useCompanyId();
  const { brandId } = useBrandId();

  const [searchParams, setSearchParams] = useSearchParams();
  const initialRange = resolveRangeFromParams(searchParams, presets, "30D");

  const [preset, setPreset] = useState<PresetKey>(initialRange.preset);
  const [startDate, setStartDate] = useState<Dayjs | null>(initialRange.start);
  const [endDate, setEndDate] = useState<Dayjs | null>(initialRange.end);
  const [processor, setProcessor] = useState<string>("");
  const [data, setData] = useState<DeflectionsDashboardResponse | null>(null);
  const processorOptions = useDeflectionProcessors();

  const handlePresetChange = (next: PresetKey) => {
    setPreset(next);
    if (next !== "custom") {
      setStartDate(presets[next].start());
      setEndDate(presets[next].end());
    }
  };

  // Keep the date range in the URL, matching `OverviewTab` and `VampRatesTab`.
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set("range", preset);
    if (startDate) params.set("start", startDate.format("YYYY-MM-DD"));
    else params.delete("start");
    if (endDate) params.set("end", endDate.format("YYYY-MM-DD"));
    else params.delete("end");
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, startDate, endDate]);

  useEffect(() => {
    if (!startDate || !endDate) return;
    const params = new URLSearchParams({
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: endDate.format("YYYY-MM-DD"),
      ...(processor && { ppType: processor }),
      ...(companyId && { companyId: companyId.toString() }),
      ...(brandId && { brandId: brandId.toString() }),
    });
    get<DeflectionsDashboardResponse>(`/api/deflections/dashboard?${params.toString()}`)
      .then(setData)
      .catch((err) => console.error("Failed to fetch deflections dashboard:", err));
  }, [startDate, endDate, processor, companyId, brandId, get]);

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <DataFilter
        preset={preset}
        startDate={startDate}
        endDate={endDate}
        onPresetChange={handlePresetChange}
        onStartDateChange={(d) => { setStartDate(d); setPreset("custom"); }}
        onEndDateChange={(d) => { setEndDate(d); setPreset("custom"); }}
        processor={processor}
        onProcessorChange={setProcessor}
        processorOptions={processorOptions}
      />
      <DeflectionsKpiRow
        kpis={data?.kpis}
        previousKpis={data?.previousKpis}
        days={startDate && endDate ? endDate.diff(startDate, "day") + 1 : 0}
      />
      <ApiCallsChart data={data?.charts.apiCalls} loading={loading} />
      <DeflectionsBarChart data={data?.charts.deflections} loading={loading} />
      {data?.internal && <DeflectionsInternalSection internal={data.internal} />}
    </Box>
  );
}
